import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/apiClient';
import { formatApiError } from '@/lib/utils';
import { X, User, Lock, Camera, Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react';

const IC = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C] transition-all";

export default function ModalPerfil({ onClose }) {
  const { user, checkAuth } = useAuth();
  const [tab, setTab] = useState('perfil');
  const [nombre, setNombre] = useState(user?.nombre || '');
  const [apellido, setApellido] = useState(user?.apellido || '');
  const [show, setShow] = useState(false);
  const [pass, setPass] = useState({ actual: '', nueva: '', confirmar: '' });
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState('');
  const [err, setErr] = useState('');
  const [subiendo, setSubiendo] = useState(false);

  const guardarNombre = async (e) => {
    e.preventDefault(); setErr(''); setSaving(true);
    try {
      await api.put(`/api/admin/usuarios/${user?.id || 'me'}/editar`, { nombre, apellido });
      await checkAuth();
      setOk('Nombre actualizado'); setTimeout(() => setOk(''), 2500);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail, 'Error')); }
    finally { setSaving(false); }
  };

  const cambiarPass = async (e) => {
    e.preventDefault(); setErr('');
    if (pass.nueva !== pass.confirmar) { setErr('Las contraseñas no coinciden'); return; }
    if (pass.nueva.length < 6) { setErr('Mínimo 6 caracteres'); return; }
    setSaving(true);
    try {
      await api.post('/api/cambiar-password-inicial', {
        nueva_password: pass.nueva,
        confirmar_password: pass.confirmar,
      });
      setPass({ actual: '', nueva: '', confirmar: '' });
      setOk('Contraseña actualizada'); setTimeout(() => setOk(''), 2500);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail, 'Error')); }
    finally { setSaving(false); }
  };

  const subirAvatar = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData(); fd.append('foto', file);
      await api.post('/api/usuarios/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await checkAuth();
      setOk('Avatar actualizado'); setTimeout(() => setOk(''), 2500);
    } catch (e) { setErr('Error al subir imagen'); }
    finally { setSubiendo(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Mi perfil</h3>
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
        </div>

        {/* Avatar */}
        <div className="flex flex-col items-center py-6 border-b border-white/5">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-[#C9A44C]/15 border-2 border-[#C9A44C]/30 flex items-center justify-center text-[#C9A44C] text-2xl overflow-hidden">
              {user?.avatar_url
                ? <img src={user.avatar_url} alt="avatar" className="w-full h-full object-cover" />
                : (user?.nombre || 'U').charAt(0).toUpperCase()}
            </div>
            <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#C9A44C] flex items-center justify-center cursor-pointer hover:bg-[#E0C878] transition-all">
              {subiendo ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#070707]" /> : <Camera className="w-3.5 h-3.5 text-[#070707]" />}
              <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => subirAvatar(e.target.files[0])} disabled={subiendo} />
            </label>
          </div>
          <div className="mt-3 text-center">
            <div className="text-sm font-medium">{user?.nombre} {user?.apellido}</div>
            <div className="text-[11px] text-[#C9A44C] uppercase tracking-wide">{user?.rol}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/5">
          {[
            { id: 'perfil', label: 'Datos', icon: User },
            { id: 'password', label: 'Contraseña', icon: Lock },
          ].map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => { setTab(id); setErr(''); setOk(''); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs transition-all border-b-2 ${
                tab === id ? 'border-[#C9A44C] text-[#C9A44C]' : 'border-transparent text-[#888]'
              }`}>
              <Icon className="w-3.5 h-3.5" />{label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 mb-3 text-red-400 text-xs">{err}</div>}
          {ok && <div className="bg-green-500/10 border border-green-500/25 rounded-lg p-2.5 mb-3 text-green-400 text-xs flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5" />{ok}</div>}

          {tab === 'perfil' && (
            <form onSubmit={guardarNombre} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-[#888] block mb-1">Nombre</label>
                  <input className={IC} value={nombre} onChange={(e) => setNombre(e.target.value)} />
                </div>
                <div>
                  <label className="text-[11px] text-[#888] block mb-1">Apellido</label>
                  <input className={IC} value={apellido} onChange={(e) => setApellido(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-[#888] block mb-1">Email</label>
                <input className={IC + ' opacity-60 cursor-not-allowed'} value={user?.email || ''} disabled />
              </div>
              <button type="submit" disabled={saving}
                className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar cambios'}
              </button>
            </form>
          )}

          {tab === 'password' && (
            <form onSubmit={cambiarPass} className="space-y-3">
              {[
                { k: 'nueva', label: 'Nueva contraseña' },
                { k: 'confirmar', label: 'Confirmar contraseña' },
              ].map(({ k, label }) => (
                <div key={k}>
                  <label className="text-[11px] text-[#888] block mb-1">{label}</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]/40" />
                    <input type={show ? 'text' : 'password'} className={IC + ' pl-11'}
                      value={pass[k]} onChange={(e) => setPass((p) => ({ ...p, [k]: e.target.value }))} required />
                    {k === 'nueva' && (
                      <button type="button" onClick={() => setShow(!show)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#888]">
                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button type="submit" disabled={saving}
                className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cambiar contraseña'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
